import stopwatch from "@/Stopwatch";
import CONSTANTS from "@/constants";

let hasInitialSyncDone = false;

const state = {
    minutes: 0,
    seconds: 0,
    isPaused: true,
    period: '1',
    settingsVisible: false,
};

const getters = {
    getDisplayTime: (state) => {
        let m = state.minutes < 10 ? "0" + state.minutes : state.minutes;
        let s = state.seconds < 10 ? "0" + state.seconds : state.seconds;
        return `${m}:${s}`;
    },
    isPaused: state => state.isPaused,
};

const mutations = {
    SET_TIME(state, { minutes, seconds }) {
        state.minutes = minutes;
        state.seconds = seconds;
    },
    SET_PAUSED(state, value) {
        state.isPaused = value;
    },
    SET_PERIOD(state, value) {
        state.period = value;
    }
};

const actions = {
    // Inicializálás: UI frissítő loop és Socket figyelés elindítása
    initClock({ commit, dispatch, rootState }) {

        // 1. UI Frissítő ciklus (requestAnimationFrame)
        // Ez olvassa ki folyamatosan a stopwatch-ból az időt és frissíti a state-et
        const tick = () => {
            if (!stopwatch.paused) { // stopwatch.paused nincs a Stopwatch.js-ben, de a stop() nullázza az intervalt
                // A Stopwatch.js-ben nincs publikus .paused property, de az interval hiánya jelzi a megállást.
                // Viszont a state.isPaused a mérvadó a UI szempontjából.
            }

            // Mindig frissítünk, ha fut az óra a state szerint, vagy ha a stopwatch jár
            // A Stopwatch.js belső állapota (interval) nem publikus, így a getTime-ra hagyatkozunk
            let totalSeconds = Math.floor(stopwatch.getTotalTime());
            let min = Math.floor(totalSeconds / 60);
            let sec = totalSeconds % 60;

            // Csak akkor commitolunk, ha változott az érték (teljesítmény optimalizálás)
            if (min !== state.minutes || sec !== state.seconds) {
                commit('SET_TIME', { minutes: min, seconds: sec });
            }

            requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);

        // 2. Socket figyelők beállítása (A hiányzó láncszem!)
        if (window.vueio) {
            // Leiratkozunk először, hogy ne legyen duplikáció hot-reload esetén
            window.vueio.off(CONSTANTS.SOCKET.ACTIONS.SYNC);

            window.vueio.on(CONSTANTS.SOCKET.ACTIONS.SYNC, (data) => {
                // Csak akkor dolgozzuk fel, ha van releváns adat
                if (!data || !data.type) return;

                // Óra indítás/megállítás
                if (data.type === 'clock_toggle') {
                    dispatch('socketClockToggle', data.content); // data.content = true/false (isPaused)
                }

                // Idő szinkronizáció
                if (data.type === 'clock_value') {
                    dispatch('socketClockValue', data.content); // data.content = { minutes, seconds, clientTime, ... }
                }
            });
        }
    },

    // UI-ról indított kapcsolás (pl. CR megnyomja a gombot)
    togglePause({ commit, state, rootState }) {
        const newState = !state.isPaused;

        // Lokális Stopwatch kezelés
        if (newState) {
            stopwatch.stop(); // Pause (true) -> Stop
        } else {
            stopwatch.start(); // Pause (false) -> Start
        }

        commit('SET_PAUSED', newState);

        // Socket küldés (csak ha CR/jegyzőkönyvvezető vagyunk)
        if (rootState.connection &&
            rootState.connection.user &&
            rootState.connection.user.type === 'cr') {

            window.vueio.emit(CONSTANTS.SOCKET.ACTIONS.SYNC, {
                type: 'clock_toggle',
                content: newState,
                clientTime: window.ts ? window.ts.now() : Date.now() // Timesync időbélyeg
            });
        }
    },

    // Socket felől érkező Pause/Start parancs
    socketClockToggle({ commit, state, rootState }, isPaused) {
        const urlParams = new URLSearchParams(window.location.search);
        const isCR = urlParams.get('felulet') === 'JEGYZOKONYV' || window.usertype === 'report';
        const isSub = window.usertype === 'sub' || urlParams.get('felulet') === 'SUB';

        // VÉDELEM: CR-ként CSAK az első betöltéskor (F5) fogadjuk el az állapotot a szervertől!
        if (isCR && hasInitialSyncDone) return;

        // JAVÍTÁS: a SUB felület órája a CR-DMR párostól TELJESEN FÜGGETLENÜL
        // mozog (üzleti követelmény) - a SUB sosem fogadhat el a CR-től érkező
        // indítás/megállítás parancsot, l. Clock.vue LASTTIMELOAD ágának azonos
        // javítását.
        if (isSub) return;

        if (isPaused) {
            stopwatch.stop();
        } else {
            stopwatch.start();
        }
        commit('SET_PAUSED', isPaused);
    },

    // Socket felől érkező Idő szinkronizáció
    // Socket felől érkező Idő szinkronizáció
    socketClockValue({ commit, state, rootState }, data) {
        const urlParams = new URLSearchParams(window.location.search);
        const isCR = urlParams.get('felulet') === 'JEGYZOKONYV' || window.usertype === 'report';
        const isSub = window.usertype === 'sub' || urlParams.get('felulet') === 'SUB';

        // A CR SOHA, SEMMILYEN KÖRÜLMÉNYEK KÖZÖTT nem fogad el időt a szervertől.
        // Ő diktálja az időt. Ha jön valami, azonnal eldobjuk!
        if (isCR) return;

        // JAVÍTÁS: a SUB felület órája a CR-DMR párostól TELJESEN FÜGGETLENÜL
        // mozog (üzleti követelmény) - a SUB sosem fogadhat el a CR-től érkező
        // idő-szinkronizációt sem, l. Clock.vue LASTTIMELOAD ágának azonos
        // javítását.
        if (isSub) return;

        // 1. Kiszámoljuk az alapadatot másodpercben
        let sentTimeInSeconds = (data.minutes * 60) + data.seconds;

        // 2. Latency (hálózati késés) kompenzáció
        if (data.clientTime && window.ts) {
            const now = window.ts.now();
            const latencyMs = now - data.clientTime;
            if (latencyMs > 0 && latencyMs < 5000) {
                const latencySec = latencyMs / 1000;
                sentTimeInSeconds += latencySec;
            }
        }

        // Mivel a szervertől kapott idő már kumulatív, vissza kell alakítanunk periódus időre a stopwatch számára.
        let periodTime = sentTimeInSeconds - stopwatch.getTrueTotalTime();
        if (periodTime < 0) periodTime = 0;

        // 3. Beállítjuk a Stopwatch-ot
        stopwatch.setTime(periodTime);

        // 4. Frissítjük a state-et
        commit('SET_TIME', { minutes: data.minutes, seconds: data.seconds });
        hasInitialSyncDone = true;
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
